import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, ValueChangeEvent } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { LogTask } from 'src/app/model/LogTask';
import { Environment, SolutionInfo } from 'src/app/model/SolutionInfo';
import { DataService } from 'src/app/service/data.service';
import { EventService } from 'src/app/service/event.service';
import { SolutionService } from 'src/app/service/solution.service';
import { LogTaskResponse, SupportService } from 'src/app/service/support.service';
import { Events, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ColumnSort, ColumnType, DataTableColumn, DataTableRow } from '../controls/datatable/datatable.component';
import { ModalControl, ModalControlType, ModalMessageComponent, ModalMessageType, ModalResult } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { FromNowPipe } from 'src/app/pipe/from-now.pipe';
import { FileSizePipe } from 'src/app/pipe/file-size.pipe';
import { WizardStep } from '../controls/wizard/wizard.component';
import { SclogsComponent } from '../dialogs/settings-support/sclogs/sclogs.component';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { TreeEvent, TreeItem } from '../controls/tree/tree.component';
import { SFTPUploadResult, SupportcaseComponent } from '../dialogs/settings-support/supportcase/supportcase.component';
import { ModalConfig } from '../controls/modal/modal-config';
import { TaskStatus } from 'src/app/model/TaskStatus';
import { SettingsSupportSFTPAuthComponent } from '../dialogs/settings-support-sftpauth/settings-support-sftpauth.component';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { DataTableTitleIcon } from '../controls/dataheaderbar/dataheaderbar.component';
import { ModalRef } from '../controls/modal/modal-ref';

//step wizard states
enum LogPackageState {
  PROD_LOGS= "productLogs",
  SC_LOGS = "systemCenterLogs",
  SUPPORT_CASE_INFO = "supportCaseInfo",
}
//product logs package table fields
export enum ProductLogPackageField {
  ID = "id",
  NAME = "prefix",
  PRODUCTNAME = "productName",
  PRODUCTVERSION = "productVersion",
  REQUESTBY = "initiatorUserName",
  CREATED = "createdDateTime",
  LASTUPDATED = "updatedDateTime",
  SIZE = "fileSize",
  STATUS = "status",
  DOWNLOAD = "downloadUrl"
}
//SFTP File table fields
export enum FTPFileField {
  TYPE = "type",
  NAME = "name",
  SIZE = "fileSize",
  MODIFIED = "modified"
}

@Component({
    selector: 'app-log-package',
    templateUrl: './log-package.component.html',
    styleUrls: ['./log-package.component.css'],
    standalone: false
})
export class LogPackageComponent implements OnInit, OnDestroy {
  scLogsStep = { 'id': LogPackageState.SC_LOGS, 'name': $localize`System Center Logs` };
  prodLogsStep = { 'id': LogPackageState.PROD_LOGS, 'name': $localize`Product Logs` };
  supportCaseInfoStep = { 'id': LogPackageState.SUPPORT_CASE_INFO, 'name': $localize`Upload` };
  steps: WizardStep[] = [this.prodLogsStep, this.scLogsStep, this.supportCaseInfoStep];
  index: number = 0;
  LogPackageState = LogPackageState;
  state: LogPackageState = LogPackageState.PROD_LOGS; //init state
  solutionInfo: SolutionInfo;
  environments: Environment[] = [];
  selectedEnvironment: Environment;
  isOKToDeactivate:boolean = false;
  solutionInfoSubscription: Subscription;
  prevButtonId:string = "";

  //Product Logs state
  productLogsPkgCols: DataTableColumn[] = Array<DataTableColumn>(
      new DataTableColumn(ProductLogPackageField.NAME, $localize`Name`, ColumnType.STRING, false, "21.5"),
      new DataTableColumn(ProductLogPackageField.PRODUCTNAME, $localize`Product`, ColumnType.STRING, false, "18.5"),
      new DataTableColumn(ProductLogPackageField.PRODUCTVERSION, $localize`Version`, ColumnType.STRING, false, "17"),
      new DataTableColumn(ProductLogPackageField.REQUESTBY, $localize`Requested By`, ColumnType.STRING, false, "15"),
      new DataTableColumn(ProductLogPackageField.LASTUPDATED, $localize`Updated`, ColumnType.DATEFROMNOW, false, "17"),
      new DataTableColumn(ProductLogPackageField.SIZE, $localize`Size`, ColumnType.FILESIZE, false, "11"),
    );
  productLogsPkgRows: DataTableRow[] = [];
  totalProductLogsPkg: number = 0;
  loadingProdLogsPkg: boolean = true;
  selectedLogsTask: LogTask[] = [];
  logTaskResponse: LogTaskResponse;
  freeDiskSpace: string = "0";
  freeDiskSpaceInBytes: number;
  productLogsMsg: string = "";
  hasSufficientDiskSpace: boolean = true;

  //System Center Logs state
  agentsLoading: boolean = false;
  agentTitle: string = $localize`Agents`;
  started: boolean = false;
  agentCols: DataTableColumn[];
  agentRows: DataTableRow[] = [];
  packageForm = new FormGroup({
    packageNameCtrl: new FormControl('', [
      Validators.required,
      Validators.pattern("[a-zA-Z0-9\-\_ ]*"), //avoid special characters in file name
      Validators.maxLength(100)
    ]),
    includeMgrLogsCtrl: new FormControl<boolean>(true, []),
    includeSysReportCtrl: new FormControl<boolean>(true, []),
    includeLandscapeCtrl: new FormControl<boolean>(true, []),
    includeAgentLogsCtrl: new FormControl<boolean>(true, []),
  });
  packageFormSubscription: Subscription;
  overrideAgentSelectAll: boolean = false;

  //support case info
  supportCaseInfoText:string = "";
  isAuthenticated:boolean = false;
  selectedLogsPackageId:string = "";
  selectedLogsPackageName:string = "";
  ftpDir:TreeItem[] = [];
  ftpSidePanelTitle:string = $localize`Remote directories`;
  ftpFileTableTitle:string = "";
  ftpFileTableTitleIcon:DataTableTitleIcon = DataTableTitleIcon.FOLDER;
  ftpFileTableCols: DataTableColumn[] = Array<DataTableColumn>(
    new DataTableColumn(FTPFileField.TYPE, $localize`Type`, ColumnType.STATUSEXT, false, "8"),
    new DataTableColumn(FTPFileField.NAME, $localize`Name`, ColumnType.STRING, true, "50", ColumnSort.ASC),
    new DataTableColumn(FTPFileField.SIZE, $localize`Size`, ColumnType.STRING, false, "12"),
    new DataTableColumn(FTPFileField.MODIFIED, $localize`Modified`, ColumnType.STRING, false, "30")
  );
  ftpFileTableRows: DataTableRow[] = [];
  ftpLoading: boolean = false;
  selectedFTPDir:TreeItem;
  ftpDirEmptyMsg:string = "";
  loadingDirectories:boolean = false;
  isSupportCaseStateReady:boolean = false;
  isUploaded:boolean = false;
  signInModelRef: ModalRef;

  eventMessageSubscription: Subscription;

  constructor(protected router: Router,
    private eventService: EventService,
    protected dataService: DataService,
    protected route: ActivatedRoute,
    protected modal: Modal,
    protected wsService: WebsocketService,
    protected solutionService: SolutionService,
    private supportService: SupportService,
    protected siService:SoftwareInventoryService,
    protected authService: AuthenticationService) {

    this.agentCols = Array<DataTableColumn>(
      new DataTableColumn("systemName", $localize`Name`, ColumnType.STRING, false, "24.5"),
      new DataTableColumn("description", $localize`Description`, ColumnType.STRING, false, "24.5"),
      new DataTableColumn("osName", $localize`Operating System`, ColumnType.STRING, false, "21"),
      new DataTableColumn("macAddress", $localize`Address`, ColumnType.STRING, false, "20"),
      new DataTableColumn("statusExt", $localize`Status`, ColumnType.STATUSEXT, false, "10")
    );

    this.selectedLogsTask =  this.router.lastSuccessfulNavigation()?.extras.state?.tasks;
    if(this.selectedLogsTask == undefined){
      this.selectedLogsTask = [];
    }
    if(this.router.lastSuccessfulNavigation()?.extras.state?.selectedLogsPackageId){
      this.goToSupportCaseInfo(this.router.lastSuccessfulNavigation()?.extras.state?.selectedLogsPackageId);
    }

    this.packageFormSubscription = this.packageForm.events.subscribe(event => {
      if (event instanceof ValueChangeEvent) {
        //unselect both agent options
        if (!event.value.includeAgentLogsCtrl && !event.value.includeLandscapeCtrl) {
          //unselect agents
          if (this.agentRows.length > 0) {
            this.overrideAgentSelectAll = false;
            this.agentRows.forEach(agent => {
              agent.selected = false;
            });
          }
        } else if (event.value.includeAgentLogsCtrl || event.value.includeLandscapeCtrl) {
          //make sure at least one agent is selected
          if (this.agentRows.length > 0) {
            if (this.getSelectedAgentCount() == 0) {
              this.overrideAgentSelectAll = true;
              this.agentRows.forEach(agent => {
                if(agent.data.isOnline){
                  agent.selected = true;
                }
              });
            }
          }
        }
      }
    });
  }

  get packageNameCtrl() { return this.packageForm.get('packageNameCtrl'); }
  get includeMgrLogsCtrl() { return this.packageForm.get('includeMgrLogsCtrl');}
  get includeSysReportCtrl() { return this.packageForm.get('includeSysReportCtrl');}
  get includeLandscapeCtrl() { return this.packageForm.get('includeLandscapeCtrl');}
  get includeAgentLogsCtrl() { return this.packageForm.get('includeAgentLogsCtrl');}

  ngOnInit(): void {
    this.solutionInfoSubscription = this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      if(this.selectedEnvironment == undefined || this.selectedEnvironment.id != newSolutionInfo.currentEnvironment.id){
        this.solutionInfo = newSolutionInfo;
        this.environments = [];

        this.solutionInfo.environments.forEach(env => {
          if (env.agents.length > 0) {
           this.environments.push(env);
          }
        });

        if(this.selectedEnvironment !== undefined){
          this.selectedLogsTask = [];
        }
        this.setSelectedEnvironment(this.solutionInfo.currentEnvironment);
        const isAgentAvailable: boolean = this.selectedEnvironment?.agents.length > 0;
        
        if(isAgentAvailable){
          this.includeAgentLogsCtrl.enable({emitEvent: false});
          this.includeAgentLogsCtrl.setValue(true, {emitEvent: false});
          this.includeLandscapeCtrl.enable({emitEvent: false});
          this.includeLandscapeCtrl.setValue(true, {emitEvent: false});
          this.overrideAgentSelectAll = true;
        }else{
          this.includeAgentLogsCtrl.disable({emitEvent: false});
          this.includeAgentLogsCtrl.setValue(false, {emitEvent: false});
          this.includeLandscapeCtrl.disable({emitEvent: false});
          this.includeLandscapeCtrl.setValue(false, {emitEvent: false});
          this.overrideAgentSelectAll = false;
        }

        this.includeMgrLogsCtrl.setValue(true, {emitEvent: false});
        this.includeSysReportCtrl.setValue(true, {emitEvent: false});

        if(this.router.lastSuccessfulNavigation()?.extras.state?.selectedLogsPackageId){
          this.goToSupportCaseInfo(this.router.lastSuccessfulNavigation()?.extras.state?.selectedLogsPackageId);
        }else{
          this.goToProdLogsPackage();
        }
      }
    });

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if (msg.eventType == Events.SFTPUploadError) {
        Utils.closeModals(this.eventService);
        this.handleSFTPUploadError();
      }
    });
  }

  ngOnDestroy() {
    this.solutionInfoSubscription?.unsubscribe();
    this.packageFormSubscription?.unsubscribe();
    this.eventMessageSubscription?.unsubscribe();
  }

  start(): void {
    this.started = true;
    //make sure product logs or system center logs are selected.
    const isProLogsSelected:boolean = this.selectedLogsTask.length > 0;
    const numAgentSelected:number = this.agentRows.filter(agent => {return agent.selected;}).length;
    const isSystemCenterLogsSelected:boolean = numAgentSelected > 0 || (numAgentSelected == 0 && (this.includeMgrLogsCtrl.value || this.includeSysReportCtrl.value));

    if(!isProLogsSelected && !isSystemCenterLogsSelected){
      const sub: Subscription =
          this.modal.open(ModalMessageComponent, {
            data: {
              title: $localize`Error`,
              type: ModalMessageType.Error,
              message: $localize`Unable to collect log packages. Please select at least one product log package or System Center log option to proceed to the next step.`,
              primaryBtn: $localize`Close`,
              width: '550px'
            },
            id: 'otsc-error-no-logs-pkg'
          }).afterClosed.subscribe(() => {
            this.started = false;
            sub.unsubscribe();
          });
    }else {
      this.validateTasks();
    }
  }

  package(waitForTheTaskToFinish:boolean): void {
    let agents: string[] = [];
    this.agentsLoading = false;
    this.agentRows.forEach(agent => {
      if(agent.selected){
        agents.push(agent.data.macAddress);
      }
    });
    let logTaskIds: string[] = [];
    this.selectedLogsTask.forEach(logsTask => {
      logTaskIds.push(logsTask.uuid);
    });

    this.supportService.packageLogs(this.selectedEnvironment.id, this.packageNameCtrl.value,
      this.includeMgrLogsCtrl.value, agents, this.includeSysReportCtrl.value, this.includeLandscapeCtrl.value, 
      logTaskIds, this.includeAgentLogsCtrl.value, !waitForTheTaskToFinish).pipe(tap(resp => {
        const status:TaskStatus = resp.body;

        if(status.started){
          if(waitForTheTaskToFinish){
            this.generateSCLogsPackage(status.dataJson.activityId);
          }else{
            this.complete();
          }
        }else {
          this.handleLogsPackageCollectionError();
        }
      }),
      catchError(resp => {
        this.started = false;
        this.handleLogsPackageCollectionError();
        return of(resp);
      })
    ).subscribe();
  }

  handleLogsPackageCollectionError(): void {
    const sub: Subscription =
          this.modal.open(ModalMessageComponent, {
            data: {
              title: $localize`Error`,
              type: ModalMessageType.Error,
              message: $localize`Unable to collect log packages. Please try again later or check the logs for details.`,
              primaryBtn: $localize`Close`,
              width: '550px'
            },
            id: 'otsc-error-log-task-msg'
          }).afterClosed.subscribe(() => {
            sub.unsubscribe();
            this.complete();
          });
  }

  cancel(): void{
    const sub: Subscription =
            this.modal.open(ModalMessageComponent, {
              data: {
                title: this.state == LogPackageState.SUPPORT_CASE_INFO ?  $localize`Cancel Log Packages Upload` : $localize`Cancel Log Packages Collection`,
                message:  this.state == LogPackageState.SUPPORT_CASE_INFO ? $localize`Are you sure you want to cancel uploading the log packages?` : $localize`Are you sure you want to cancel collecting the log packages?`,
                type: ModalMessageType.Question,
                primaryBtn: $localize`Yes`,
                secondaryBtn: $localize`No`,
                width: '475px'
              },
              id: 'otsc-log-package-cancel'
            }
            ).afterClosed.subscribe((result: boolean) => {
              if (result) {
                this.authService.disconnectToSFTP().subscribe(); //fire and forget
                this.complete();
              }
              sub.unsubscribe();
            });
  }

  setSelectedEnvironment(env: Environment): void{
    this.selectedEnvironment = env;
    this.getAgents();
  }

  getAgents(): void {
    let tmpAgentRows: DataTableRow[] = [];
    this.selectedEnvironment.agents.forEach(agent => {
        if (agent.description == undefined || agent.description == 'undefined'){
          agent.description = '';
        }
        agent.statusExt = Utils.getAgentStatus(agent);
        const tmpAgentRow:DataTableRow = new DataTableRow(agent, [], false, true);
        //tmpAgentRow.disableCheckbox = !agent.isOnline;
        tmpAgentRows.push(tmpAgentRow);
    });
    this.agentRows = tmpAgentRows;
  }

  selectAgent(row: DataTableRow):void {
    const numSelectedAgents:number = this.getSelectedAgentCount();
    //update agent log options based on agent selection
    if(this.includeLandscapeCtrl.enabled){
      this.includeLandscapeCtrl.setValue(numSelectedAgents > 0);
    }
    if(this.includeAgentLogsCtrl.enabled){
      this.includeAgentLogsCtrl.setValue(numSelectedAgents > 0);
    }
  }

  getSelectedAgentCount(): number {
    return this.agentRows?.filter(agent => {
              return agent.selected;
            }).length;
  }

  validateTasks(): void {
    this.supportService.getLogTasks(100, 0).pipe(
      tap(resp => {
        this.logTaskResponse = resp.body;
        if(this.logTaskResponse.count >= 5){
          let nextTaskToDelete: LogTask = this.getNextLogToDelete(this.logTaskResponse);
          if(nextTaskToDelete == undefined){
            this.openQueuedMessageDialog();
          } else{
            this.openDeleteLastLogDialog(this.logTaskResponse.tasks[this.logTaskResponse.tasks.length - 1]);
          }
        }else{
          this.collectSupportLogsPackage();
        }
      })
    ).subscribe();
  }

  collectSupportLogsPackage(): void {
    let checkControl: ModalControl = new ModalControl('waitForTheTaskToFinish', ModalControlType.CHECKBOX, $localize`Stay on this page`, true);
    const sub: Subscription =
              this.modal.open(ModalMessageComponent, {
                data: {
                  title: $localize`Collecting Log Packages`,
                  type: ModalMessageType.Question,
                  message: $localize`System Center Manager is preparing to gather log packages. This process will capture basic information about the enabled products in your environment. To help diagnose the issue effectively, we highly recommend selecting all log options. This process may take some time and will run in the background. Please stay on this page to upload the log packages to the SFTP site in the next step. Alternatively, you can uncheck the checkbox to leave this page and upload the log packages later once the task is complete.`,
                  primaryBtn: $localize`Collect`,
                  secondaryBtn: $localize`Cancel`,
                  width: '625px',
                  useResult: true,
                  controls: [checkControl]
                },
                id: 'otsc-collect-logs-pkg'
              }).afterClosed.subscribe((result: ModalResult) => {
                if (result.isPrimary) {
                  this.package(result.controls[0].val);
                }else{
                  this.started = false;
                }
                sub.unsubscribe();
              });
  }

  getNextLogToDelete(resp: LogTaskResponse): LogTask{
    let task: LogTask = undefined;
    for (let i = resp.tasks.length - 1; i > -1; i--){
      if (resp.tasks[i].status == "ERROR" || resp.tasks[i].status == "COMPLETED" || resp.tasks[i].status == "CANCELED"){
        task = resp.tasks[i];
        break;
      }
    }
    return task;
  } 

  openQueuedMessageDialog() : void{
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Error`,
          type: ModalMessageType.Error,
          message: $localize`A maximum of five log packages can be stored at a time. There are currently too many tasks queued, your task cannot be started at this time. You can cancel any of the queued log tasks on the Activities page.`,
          primaryBtn: $localize`Close`,
          width: '550px'
        },
        id: 'otsc-delete-log-task-msg'
      }).afterClosed.subscribe(() => {
        this.started = false;
        sub.unsubscribe();
      });
  }

  openDeleteLastLogDialog(task: LogTask): void{
    let formattedData = new FromNowPipe().transform(task.createdDateTime.toString());
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Delete`,
          type: ModalMessageType.Question,
          message: $localize`A maximum of five log packages can be stored at a time. To complete this request the package '${task.prefix}' created ${formattedData} must be deleted.<br/>Are you sure you want to delete this package?<br/>`,
          primaryBtn: $localize`Delete`,
          secondaryBtn: $localize`Cancel`,
          width: '550px'
        },
        id: 'otsc-delete-log-task-msg'
      }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.supportService.deleteTasks([task.uuid], this.solutionInfo.currentEnvironment.id).pipe(
            tap(resp => {
              this.collectSupportLogsPackage();
            })
          ).subscribe();
        }else{
          this.started = false;
        }
        sub.unsubscribe();
      });
  }

  isReadyToStart(): boolean {
    let isReady: boolean = false;
    if(!this.started){
      if(this.packageForm.valid && (this.getSelectedAgentCount() > 0 || this.includeMgrLogsCtrl.value || this.includeSysReportCtrl.value)){
        isReady = true;
      }
    }
    return isReady;
  }

  generateSCLogsPackage(activityId:number):void {
    //show banner with progress
    const sub: Subscription = this.modal.open(SclogsComponent, {
      id: 'otsc-sc-logs-package-progress',
      data: {
        activityId: activityId
      }
    }).afterClosed.subscribe((data: any) => {
      if(data.isSuccess){
        if(data.taskId != ""){
          this.goToSupportCaseInfo(data.taskId);
        }else{
          this.complete();
        }
      }else{
        this.handleLogsPackageCollectionError();
      }
      sub.unsubscribe();
    });
  }

  selectAllProdLogs(rows:DataTableRow[]):void {
    rows.forEach((row:DataTableRow) => {
      this.selectSingleProdLog(row);
    });
    this.setProdLogsMsg();
  }

  selectSingleProdLog(row: DataTableRow):void {
    if(row.selected){
      this.selectedLogsTask.push(row.data);
    }else{
      let index:number = this.selectedLogsTask.findIndex(log => log.uuid == row.data.uuid);
      
      if(index > -1){
        this.selectedLogsTask.splice(index, 1);
      }
    }
    this.setProdLogsMsg();
  }

  goToSCLogsPackage(): void {
    this.prevButtonId = 'logs-pkg-sclogs-back-button';
    this.started = false;
    this.state = LogPackageState.SC_LOGS;
    this.index = this.steps.indexOf(this.scLogsStep);
  }

  goToProdLogsPackage(): void {
    this.getProdLogsPkg();
    this.index = this.steps.indexOf(this.prodLogsStep);
  }

  getProdLogsPkg(): void {
    this.loadingProdLogsPkg = true;
    this.supportService.getManagerDiskSpace().pipe(
      tap(resp => {
        let sysInfo = resp.body;
        if (sysInfo !== undefined) {
          this.freeDiskSpace = new FileSizePipe().transform(sysInfo.diskFree);
          this.freeDiskSpaceInBytes = sysInfo.diskFree;
        }
        this.siService.getProdLogTasks(this.solutionInfo.currentEnvironment.id, '', 'COMPLETED').pipe(
          tap(resp => {
            this.logTaskResponse = resp.body;
            this.totalProductLogsPkg = this.logTaskResponse.count;

            var tmpRows: DataTableRow[] = [];
            this.logTaskResponse.tasks.forEach(task => {
              const isSelected: boolean = this.selectedLogsTask?.findIndex(log => log.uuid == task.uuid) > -1;
              tmpRows.push(new DataTableRow(task, [], false, isSelected));
            });

            this.productLogsPkgRows = tmpRows;
          }),
           finalize(() => {
            this.loadingProdLogsPkg = false;
            this.state = LogPackageState.PROD_LOGS;
            this.setProdLogsMsg();
          })
        ).subscribe();
      })
    ).subscribe();
  }

  setProdLogsMsg(): void {
    const selectedRows: DataTableRow[] = this.productLogsPkgRows.filter((row: DataTableRow) => {
      return row.selected;
    });
    let selectedProdLogsSizeInBytes:number = 0;
    selectedRows.forEach((row:DataTableRow) => {
      selectedProdLogsSizeInBytes += row.data.fileSize;
    });
    const selectedProdLogsSize:string = new FileSizePipe().transform(selectedProdLogsSizeInBytes);

    if(selectedProdLogsSizeInBytes >= this.freeDiskSpaceInBytes * 0.8) {
      this.hasSufficientDiskSpace = false;
      this.productLogsMsg = $localize`You have <b>${this.freeDiskSpace}</b> of disk space available, but <b>${selectedProdLogsSize}</b> is needed for the selected log packages. To successfully collect the logs, additional space beyond the required ${selectedProdLogsSize} is recommended. Please free up some space to proceed.`;
    }else{
      this.hasSufficientDiskSpace = true;
      this.productLogsMsg = $localize`The following list displays the product log packages for the current environment (<b>${this.selectedEnvironment.solName + ' : ' + this.selectedEnvironment.name}</b>) that were created successfully. <b>Note:</b> Please make sure you have enough disk space before selecting the product log packages. You currently have <b>${this.freeDiskSpace}</b> of disk space available.`;
    }
  }

  goToSupportCaseInfo(logsPkgId:string): void {
    this.prevButtonId = undefined;
    this.state = LogPackageState.SUPPORT_CASE_INFO;
    this.index = this.steps.indexOf(this.supportCaseInfoStep);
    this.isSupportCaseStateReady = false;
    this.supportService.getLogsPackageFileName(logsPkgId).pipe(
      tap(resp => {
        if(resp.body.status.success){
          this.selectedLogsPackageName = resp.body.result.data;
          this.selectedLogsPackageId = logsPkgId;
          this.revertToSignInState();
          this.signIn();
        }else{
          //show error and go back to Settings > Support
          const sub: Subscription =
            this.modal.open(ModalMessageComponent, {
              data: {
                title: $localize`Log Package not found`,
                type: ModalMessageType.Error,
                message: $localize`The selected logs package is not found. Please try again. If the issue persists, check the log for more details.`,
                primaryBtn: $localize`Ok`,
                width: '550px'
              },
              id: 'otsc-error-logspackage-not-found'
            }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
              if(isPrimaryBtnClicked){
                this.complete();
              }
              sub.unsubscribe();
            });
        }
      }),
      finalize(() => {
        this.isSupportCaseStateReady = true;
      })
    ).subscribe();
  }

  signIn(): void {
    this.revertToSignInState();

    //is using supported proxy setting?
    if(this.solutionInfo.settings.isNTLMProxy){
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Unsupported Proxy Authentication Type`,
            type: ModalMessageType.Error,
            message: $localize`Your proxy setting is configured to NTLM authentication, which is not supported. Only 'Basic/Digest' or 'None' authentication types are supported.`,
            primaryBtn: $localize`Ok`,
            width: '550px'
          },
          id: 'otsc-error-unsupported-proxy'
        }).afterClosed.subscribe(() => {
          sub.unsubscribe();
        });
    }else{
      if (this.signInModelRef) {
        this.signInModelRef.close();
      }
      this.signInModelRef = this.modal.open(SettingsSupportSFTPAuthComponent, { id: 'otsc-sftp-login-dialog' });
      const sub: Subscription = this.signInModelRef.afterClosed.subscribe((isLoggedIn: boolean) => {
        if (isLoggedIn) {
          this.isAuthenticated = true;
          this.listRootDirectories();
        }
        sub.unsubscribe();
      });
    }
  }

  expandTree(treeEvent: TreeEvent): void {
    if (treeEvent.item.canExpand && !this.isUploaded) {      
      this.listDirectory(treeEvent.item);

      if (treeEvent.item.expand) {
        treeEvent.item.expand = !treeEvent.item.expand;
      } else if (treeEvent.item.getChildren().length > 0) {
        treeEvent.item.expand = true;
      } else {
        treeEvent.item.expand = true;
      }

      this.unSelectAll(this.ftpDir);
      treeEvent.item.selected = true;
      this.selectedFTPDir = treeEvent.item;
      this.supportCaseInfoText = $localize`You have selected the folder '${treeEvent.item.data.path}'. If you are ready to upload, click the 'Upload' button.`;
    }
  }

  refreshSFTPTree(): void {
    const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Confirm Refresh Remote Directories`,
            type: ModalMessageType.Question,
            message: $localize`To reload the remote root directory, you need to sign in again. Please note that your folder selection will be reset. Do you want to proceed with refreshing the remote root directories?`,
            primaryBtn: $localize`Refresh`,
            secondaryBtn: $localize`Cancel`,
            width: '550px'
          },
          id: 'otsc-refresh-tree'
        }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            this.revertToSignInState();
            this.authService.disconnectToSFTP().subscribe(); //fire and forget
            this.signIn();
          }
          sub.unsubscribe();
        });
  }

  listRootDirectories(): void {
    let rootFiles:TreeItem[] = [];
    this.ftpDir = [];
    this.loadingDirectories = true;
    this.ftpDirEmptyMsg = $localize`Loading...`;
    //list root directories
    this.supportService.listDirectoryContent("").pipe(
      tap(resp => {
        //show the latest folder first
        for (let i = resp.body.result.data.length - 1; i >= 0; i--) {
          if (resp.body.result.data[i].isDir) {
            resp.body.result.data[i].path = resp.body.result.data[i].name;
            this.ftpDir.push(new TreeItem(resp.body.result.data[i].name, { class: 'otsc-icon-mime-folder', altText: $localize`Directory` }, null, resp.body.result.data[i], true));
          } else {
            rootFiles.push(new TreeItem(resp.body.result.data[i].name, { class: 'otsc-icon-mime-document', altText: $localize`Directory` }, null, resp.body.result.data[i], false));
          }
        }

        //add root files after the folders
        rootFiles.forEach((file: TreeItem) => {
          this.ftpDir.push(file);
        });
      }),
      catchError(resp => {
        this.handleSFTPError(resp);
        return of(resp);
      }),
      finalize(() => {
        this.loadingDirectories = false;
        this.ftpDirEmptyMsg = $localize`There are no items to display.`;
        this.supportCaseInfoText = $localize`Please select the folder for your support case number.`;
      })
    ).subscribe();
  }

  listDirectory(treeItem:TreeItem, disconnect:boolean = false, leavePage:boolean = false): void {
    treeItem.loading = true;
    treeItem.emtpyChildren();
    this.ftpFileTableRows = [];
    this.ftpLoading = true;
    this.ftpFileTableTitle = treeItem.data.path;
    //show expanded tree
    this.supportService.listDirectoryContent(treeItem.data.path).pipe(
      tap(resp => {
        for (let i = 0; i < resp.body.result.data.length; i++) {
          if (resp.body.result.data[i].isDir) {
            resp.body.result.data[i].path = treeItem.data.path + "/" + resp.body.result.data[i].name;
            treeItem.addChild((new TreeItem(resp.body.result.data[i].name, { class: 'otsc-icon-mime-folder', altText: $localize`Directory` }, null, resp.body.result.data[i], true)));
          } else {
            this.ftpFileTableRows.push(new DataTableRow({
              type: { className: 'otsc-icon-mime-document', altText: $localize`File` },
              name: resp.body.result.data[i].name,
              fileSize: new FileSizePipe().transform(resp.body.result.data[i].size),
              modified: resp.body.result.data[i].modifiedDateTime
            },
              [], false, false));
          }
        }
      }),
      catchError(resp => {
        this.handleSFTPError(resp, leavePage);
        return of(resp);
      }),
      finalize(() => {
        this.ftpLoading = false;
        treeItem.loading = false;
        if(disconnect){
          this.authService.disconnectToSFTP().subscribe();
        }
      })
    ).subscribe();
  }

  revertToSignInState(): void {
    this.selectedFTPDir = undefined;
    this.isAuthenticated = false;
    this.ftpDirEmptyMsg = $localize`Please sign in to upload log packages.`;
    this.supportCaseInfoText = $localize`Please select the folder named after your support case number. If the folder is missing, verify that you've opened a support case for files over 25 MB or generated an SFTP transfer.`;
    this.ftpFileTableRows = [];
    this.ftpDir = [];
  }

  handleSFTPError(resp:Response, leavePage:boolean = false): void {
    this.revertToSignInState();

    if (resp.status === 401) {
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Authentication Failed`,
            type: ModalMessageType.Error,
            message: $localize`You have entered an invalid user ID or password, or your account might be locked. Please try again. If the issue persists, check the System Center Manager log file for more details.`,
            primaryBtn: $localize`Ok`,
            width: '550px'
          },
          id: 'otsc-error-sftp-auth'
        }).afterClosed.subscribe(() => {
          this.goToSupportCaseInfo(this.selectedLogsPackageId);
          sub.unsubscribe();
        });
    } else if (resp.status === 440) {
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Session Expired`,
            type: ModalMessageType.Error,
            message: $localize`Your SFTP session has expired. Please sign in again. If the issue persists, check the System Center Manager log file for more details.`,
            primaryBtn: $localize`Ok`,
            width: '550px'
          },
          id: 'otsc-error-sftp-session'
        }).afterClosed.subscribe(() => {
          if(leavePage){
            this.complete();
          }else{
            this.goToSupportCaseInfo(this.selectedLogsPackageId);
          }
          sub.unsubscribe();
        });
    } else if (resp.status === 500) {
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Error Listing Directory Content`,
            type: ModalMessageType.Error,
            message: $localize`An error occurred while listing the directory content. Please sign in and try again. If the issue persists, check the System Center Manager log file for more details.`,
            primaryBtn: $localize`Ok`,
            width: '550px'
          },
          id: 'otsc-error-sftp-listing'
        }).afterClosed.subscribe(() => {
          this.goToSupportCaseInfo(this.selectedLogsPackageId);
          sub.unsubscribe();
        });
    }
  }

  unSelectAll(treeItems:TreeItem[]) {
    if(treeItems && treeItems.length > 0){
      treeItems.forEach((dir) => {
        if(dir.selected){
          dir.selected = false;
        }
        if(dir.getChildren().length > 0){
          this.unSelectAll(dir.getChildren());
        }
      });
    }
  }

  upload(): void {
    //show upload progress
    this.isUploaded = false;
    const sub: Subscription = this.modal.open(SupportcaseComponent, {
      id: 'otsc-sc-logs-package-upload-progress',
      data: {logsPkgId: this.selectedLogsPackageId, path: this.selectedFTPDir.data.path, logsPkgName: this.selectedLogsPackageName }
    }).afterClosed.subscribe((isUploaded:SFTPUploadResult) => {
      if(isUploaded == SFTPUploadResult.UPLOADED){
        this.supportCaseInfoText = $localize`The log package '${this.selectedLogsPackageName}' has been successfully uploaded to the folder '${this.selectedFTPDir.data.path}'.`;
        //refresh the selected directory
        setTimeout(() => {
          this.listDirectory(this.selectedFTPDir, true, true);
        }, 100);
        this.isUploaded = true;
      }else if(isUploaded == SFTPUploadResult.CANCELED){
        this.complete();
      }else if(isUploaded == SFTPUploadResult.FAILED){
        this.handleSFTPUploadError();
      }
      sub.unsubscribe();
    });
  }

  handleSFTPUploadError(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Error`,
          type: ModalMessageType.Error,
          message: $localize`An error occurred while uploading the log packages. Please try again later. If the issue persists, check the System Center Manager log file for more details.`,
          primaryBtn: $localize`Close`,
          width: '550px'
        },
        id: 'otsc-sftp-upload-error'
      }).afterClosed.subscribe(() => {
        this.complete();
        sub.unsubscribe();
      });
  }

  next(skip:boolean): void {
    switch(this.state){
      case LogPackageState.PROD_LOGS:
        this.goToSCLogsPackage();
        break;
      case LogPackageState.SC_LOGS:
        this.start();
        break;
      case LogPackageState.SUPPORT_CASE_INFO:
        if(!skip){
          const supportDlgSub: Subscription = this.modal.open(ModalMessageComponent, {
            data: {
              title: $localize`Upload Log Packages Confirmation`,
              type: ModalMessageType.Question,
              message: $localize`Your log package (<b>${this.selectedLogsPackageName}</b>) will be uploaded to <b>${this.selectedFTPDir.data.path}</b>. 
                                If a log package with the same name already exists, it will be overwritten. 
                                The upload may take some time depending on network traffic.`,
              primaryBtn: $localize`Upload`,
              secondaryBtn: $localize`Cancel`,
              width: '600px'
            },
            id: 'otsc-sc-logs-package-upload-confirm'
          }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
            if(isPrimaryBtnClicked){
              this.upload();
            }
            supportDlgSub.unsubscribe();
          });
        }else{
          this.complete();
        }
        break;
    }
  }

  back(): void {
    switch(this.state){
      case LogPackageState.SC_LOGS:
        this.goToProdLogsPackage();
        break;
    }
  }

  complete(): void {
    this.isOKToDeactivate = true;
    this.router.navigate(["/settings/support"]);
  }

  canDeactivate() : boolean{
    return (this.state == LogPackageState.PROD_LOGS && this.selectedLogsTask.length == 0) || this.isOKToDeactivate;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-support-logs-leave-warning');
  }
}